import { BrandLogo, IconArrowTopRight } from '@assets/icons'
import {
  Avatar,
  Box,
  Container,
  Flex,
  Link,
  LinkProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import { Heading, Text } from '@components/Common'
import { useLogout } from '@hooks/useLogout'
import { usePubSelector } from '@redux/hook'
import { ROUTE_PATHS } from '@routes/index'
import { useCallback } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

export const NavLink = ({
  children,
  active,
  ...rest
}: LinkProps & { to: string; active?: boolean }) => (
  <Link
    data-testid="nav-link"
    as={RouterLink}
    ml="50px"
    color={active ? 'primary.EgyptianBlue' : 'primary.blue'}
    fontWeight={active ? 'bold' : undefined}
    position="relative"
    _hover={{
      textDecoration: 'none',
    }}
    _before={{
      content: active ? '""' : undefined,
      position: 'absolute',
      top: '43px',
      w: 'full',
      border: '1px solid var(--chakra-colors-primary-EgyptianBlue)',
      rounded: '2px 2px 0 0',
    }}
    {...rest}
  >
    {children}
  </Link>
)

export default function Navbar() {
  const auth = usePubSelector((state) => state.auth.auth)
  const { pathname } = useLocation()
  const { logout } = useLogout()
  const navigate = useNavigate()

  const navigateToProfilePage = useCallback(
    () => navigate(ROUTE_PATHS.PROFILE),
    [],
  )

  return (
    <Box bg="white" p="20px">
      <Container>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex justifyContent="flex-start">
            <Flex alignItems="center" justifyContent="space-between">
              <Box mr="28px">
                <BrandLogo width={150} />
              </Box>
              <Box>
                <i>
                  <Heading
                    as="h7"
                    m="0"
                    color="primary.blue"
                    fontSize="14px"
                    lineHeight="18px"
                  >
                    MySmartWaterMeter
                  </Heading>
                </i>
              </Box>
            </Flex>
            <Flex justifyContent="flex-start" alignItems="center">
              {auth && (
                <>
                  <NavLink
                    to={ROUTE_PATHS.DASHBOARD}
                    active={pathname === ROUTE_PATHS.DASHBOARD}
                  >
                    <Text fontWeight="inherit">Dashboard</Text>
                  </NavLink>
                  <NavLink
                    to={ROUTE_PATHS.WATER_ACCOUNT}
                    active={pathname === ROUTE_PATHS.WATER_ACCOUNT}
                  >
                    <Text fontWeight="inherit">Water Account</Text>
                  </NavLink>
                  <NavLink
                    to={ROUTE_PATHS.LEAKS}
                    active={pathname === ROUTE_PATHS.LEAKS}
                  >
                    <Text fontWeight="inherit">Leaks</Text>
                  </NavLink>
                  <NavLink
                    to={ROUTE_PATHS.RESOURCES}
                    active={pathname === ROUTE_PATHS.RESOURCES}
                  >
                    <Text fontWeight="inherit">Resources</Text>
                  </NavLink>
                </>
              )}
              {!auth && (
                <NavLink
                  to={ROUTE_PATHS.TERM_AND_CONDITION}
                  data-testid="link-term-and-condition"
                >
                  <Text display="flex" alignItems="baseline">
                    Privacy Statement and Terms of Use&nbsp;
                    <IconArrowTopRight />
                  </Text>
                </NavLink>
              )}
            </Flex>
          </Flex>
          <Flex alignItems="center">
            {auth && (
              <Menu>
                <MenuButton data-testid="auth-menu-dropdown">
                  <Flex alignItems="center">
                    <Avatar
                      size="sm"
                      fontWeight="bold"
                      bg="primary.blue"
                      color="white"
                      name={auth.name}
                    />
                  </Flex>
                </MenuButton>
                <MenuList alignItems="center" p="0" minWidth="max-content">
                  <MenuItem
                    data-testid="button-go-to-update-profile"
                    onClick={navigateToProfilePage}
                    fontSize="14px"
                    color="grey.100"
                  >
                    Edit Profile
                  </MenuItem>
                  <MenuItem
                    data-testid="button-logout"
                    onClick={logout}
                    fontSize="14px"
                    color="grey.100"
                  >
                    Logout
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </Flex>
        </Flex>
      </Container>
    </Box>
  )
}
